"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";

import { api, humanMessage } from "../api";
import { clearSession, setSessionUser, useSession } from "../session";
import type { MallUser, MeStats } from "../types";
import { useAsync } from "../useAsync";
import { fieldErrorsOf } from "./shared";

/*
 * 내 정보 · 비밀번호.
 *
 * 사용:
 *   const me = useMe();                // me.stats.data · await me.save({ name, mobile }) → 참/거짓 · me.logout()
 *   const pw = useChangePassword();    // await pw.change(current, next)
 *
 * 🔑 사용자 자체(`user`)는 세션 저장소가 정본입니다 — `useRequireMember()` 가 돌려주는
 *    `session.user` 를 쓰세요. 저장이 성공하면 이 훅이 세션을 새 값으로 바꿔 둡니다.
 */
export function useMe() {
  const session = useSession();
  const router = useRouter();

  const stats = useAsync(
    (signal) => api.get<MeStats>("/me/stats", { signal }),
    "me-stats",
    session.status === "member",
  );

  /* 🔑 저장과 로그아웃이 `busy` 하나를 같이 씁니다 — 저장 중에 로그아웃이 눌리지 않게. */
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});

  function reset() {
    setError("");
    setFields({});
  }

  /** 🔑 휴대폰을 비우면 «지운다» 는 뜻입니다 — 빈 문자열을 그대로 보냅니다. */
  async function save(input: { name: string; mobile: string }): Promise<boolean> {
    if (busy) return false;

    setBusy(true);
    setError("");
    setFields({});

    try {
      const updated = await api.patch<MallUser>("/me", { name: input.name.trim(), mobile: input.mobile.trim() });
      setSessionUser(updated);
      return true;
    } catch (cause) {
      setFields(fieldErrorsOf(cause, ["name", "mobile"]));
      setError(humanMessage(cause));
      return false;
    } finally {
      setBusy(false);
    }
  }

  /** 🔴 서버 호출이 실패해도 이 기기에서는 로그아웃합니다 — BFF 가 쿠키를 지우는 것이 본론입니다. */
  async function logout(): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/logout", {}, { keepOnUnauthorized: true });
    } catch {
      /* 위 주석 */
    }
    clearSession();
    router.replace("/");
  }

  return { stats, save, logout, busy, error, fields, reset };
}

/**
 * 🔴 **바꾸면 다른 기기의 로그인이 모두 풀립니다** — 화면이 미리 알려야 합니다.
 * 🔑 «새 비밀번호 한 번 더» 는 화면에서만 대조합니다(서버는 두 번 받지 않습니다).
 *    성공하면 `router.replace("/me")`.
 */
export function useChangePassword() {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});

  async function change(currentPassword: string, password: string): Promise<void> {
    if (busy) return;

    setBusy(true);
    setError("");
    setFields({});

    try {
      await api.post("/me/password", { current_password: currentPassword, password });
      router.replace("/me");
    } catch (cause) {
      setFields(fieldErrorsOf(cause, ["current_password", "password"]));
      setError(humanMessage(cause));
      setBusy(false);
    }
  }

  return { change, busy, error, fields };
}
